/**
 * Add-device dialog: parent clicks "+ Add device" on the dashboard, gets a
 * short human-readable enrollment code, and reads it to the installer running
 * on their child's laptop. The code is short-lived (see sub-plan 005) so we
 * show a relative "expires in Xm" hint.
 */

import { useState } from 'preact/hooks';
import { Button } from './Button';
import { Dialog } from './Dialog';
import {
  generateEnrollmentCode,
  type EnrollmentCode,
} from '../data/enrollmentCode';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function formatExpiresIn(expiresAt: Date): string {
  const deltaMs = expiresAt.getTime() - Date.now();
  const deltaMin = Math.round(deltaMs / 60_000);
  if (deltaMin <= 0) return 'expired';
  return `expires ${RTF.format(deltaMin, 'minute')}`;
}

export function AddDeviceDialog({ open, onOpenChange }: Props) {
  const [code, setCode] = useState<EnrollmentCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function onGenerate(): Promise<void> {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const next = await generateEnrollmentCode();
      setCode(next);
    } catch {
      setError('Could not generate a code. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onCopy(): Promise<void> {
    if (code === null) return;
    await navigator.clipboard.writeText(code.code);
    setCopied(true);
  }

  function onClose(): void {
    setCode(null);
    setError(null);
    setCopied(false);
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add a device"
      description="Enroll your child's laptop with a one-time code."
    >
      <div class="space-y-4">
        <p class="text-sm text-surface-fg">
          Install Backstop on your child's laptop, then paste this code when the
          installer asks for it.{' '}
          <a
            href="/download"
            class="text-brand-500 underline hover:text-brand-600"
          >
            Download the installer
          </a>
          .
        </p>

        {code === null ? (
          <div class="flex justify-center">
            <Button onClick={onGenerate} disabled={busy}>
              {busy ? 'Generating…' : 'Generate code'}
            </Button>
          </div>
        ) : (
          <div class="space-y-3">
            <div class="rounded-lg bg-surface-muted/10 p-6 text-center overflow-hidden">
              <span class="block text-xl font-mono tracking-wider break-all">
                {code.code}
              </span>
            </div>
            <div class="text-center text-xs text-surface-muted">
              {formatExpiresIn(code.expiresAt)}
            </div>
            <div class="flex justify-center gap-2">
              <Button variant="secondary" onClick={onCopy}>
                Copy code
              </Button>
              <Button variant="ghost" onClick={onGenerate} disabled={busy}>
                {busy ? 'Generating…' : 'New code'}
              </Button>
            </div>
            {copied && (
              <div class="text-center text-xs text-ok-500" role="status">
                Copied to clipboard
              </div>
            )}
          </div>
        )}

        {error !== null && (
          <div class="text-center text-sm text-danger-500" role="alert">
            {error}
          </div>
        )}

        <div class="flex justify-end pt-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
